import React, { useEffect, useState } from 'react';
import { Button, Empty, Spin, Tag } from 'antd';
import {
  EditOutlined,
  FileTextOutlined,
  RightOutlined,
  RiseOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { analysisAPI, essayAPI } from '../../services/api';
import { Essay } from '../../types';
import StatusTag from '../../components/StatusTag';
import { getTopicFromEssay } from '../../utils/topicHelper';

const H5HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEssays: 0,
    averageScore: 0,
    latestScore: 0,
    improvement: 0,
  });
  const [recentEssays, setRecentEssays] = useState<Essay[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsRes, essaysRes] = await Promise.all([
          analysisAPI.getStats(),
          essayAPI.list({ page: 1, page_size: 4 }),
        ]);

        if (statsRes.data) {
          setStats({
            totalEssays: statsRes.data.total_essays || 0,
            averageScore: statsRes.data.average_score || 0,
            latestScore: statsRes.data.latest_score || 0,
            improvement: statsRes.data.recent_progress || 0,
          });
        }
        setRecentEssays(essaysRes.data.data || []);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="h5-page">
      <section className="h5-hero-card">
        <div className="h5-hero-copy">
          <span className="h5-hero-eyebrow">英语作文批改系统</span>
          <h1>今天先改一篇作文</h1>
        </div>
        <Button type="primary" icon={<EditOutlined />} onClick={() => navigate('/h5/essay/new')}>
          新建批改
        </Button>
        <div className="h5-hero-visual" aria-hidden="true">
          <div className="h5-hero-paper" />
          <div className="h5-hero-pencil" />
        </div>
      </section>

      <section className="h5-stats-grid">
        <div className="h5-stat-tile">
          <FileTextOutlined />
          <span>总作文数</span>
          <strong>{stats.totalEssays}</strong>
        </div>
        <div className="h5-stat-tile">
          <TrophyOutlined />
          <span>平均分数</span>
          <strong>{stats.averageScore.toFixed(1)}</strong>
        </div>
        <div className="h5-stat-tile">
          <EditOutlined />
          <span>最新分数</span>
          <strong>{stats.latestScore.toFixed(1)}</strong>
        </div>
        <div className="h5-stat-tile">
          <RiseOutlined />
          <span>最近进步</span>
          <strong className={stats.improvement >= 0 ? 'positive' : 'negative'}>
            {stats.improvement.toFixed(1)}
          </strong>
        </div>
      </section>

      <section className="h5-panel">
        <div className="h5-section-title">
          <h2>最近批改</h2>
          <Button type="text" onClick={() => navigate('/h5/history')}>
            全部 <RightOutlined />
          </Button>
        </div>
        <Spin spinning={loading}>
          {recentEssays.length === 0 ? (
            <Empty description="暂无作文记录" />
          ) : (
            <div className="h5-essay-list">
              {recentEssays.map((essay) => {
                const topic = getTopicFromEssay(essay);
                return (
                  <button
                    key={essay.id}
                    className="h5-essay-card"
                    onClick={() => navigate(`/h5/essay/${essay.id}/result`)}
                  >
                    <div>
                      <h3>{topic?.title || '未设置题目'}</h3>
                      <p>{essay.student?.name || '未分配学生'} · {essay.class?.name || '未分配班级'}</p>
                    </div>
                    <div className="h5-essay-card-side">
                      {essay.correction?.total_score ? (
                        <Tag color="green">{essay.correction.total_score} 分</Tag>
                      ) : (
                        <StatusTag status={essay.status} />
                      )}
                      <RightOutlined />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Spin>
      </section>
    </div>
  );
};

export default H5HomePage;
